"use client"

import { useMemo, useState, type ReactNode } from "react"
import {
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
  FileText,
  Image as ImageIcon,
  AlertCircle,
} from "lucide-react"
import { Message } from "./types"
import { FeedbackDialog } from "./FeedbackDialog"
import { getToolRenderer } from "@/hooks/useToolRenderer"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { ArtifactCard } from "./ArtifactCard"
import { Message as MessageRow, MessageContent } from "@/components/ai-elements/message"
import { BrandLoader } from "@/components/ai-elements/loader"
import { Reasoning } from "@/components/ai-elements/reasoning"
import { groupReasoningForDisplay } from "./groupReasoning"
import { copy } from "@/lib/copy"
import { formatMessageTime } from "@/lib/formatMessageTime"

interface ChatMessageProps {
  message: Message
  sessionId: string
  /** Display name for the signed-in user (shown above user bubbles). */
  userName?: string
  /** In-session image thumbnail URLs for this message (live preview only). */
  imagePreviews?: string[]
  onFeedbackSubmit: (feedbackType: "positive" | "negative", comment: string) => Promise<void>
  /** Click-time download-URL resolver factory, keyed by artifact (runId, name). */
  onResolveDownloadUrl?: (runId?: string, name?: string) => (() => Promise<string>) | null
}

/** Tamaño legible del adjunto: "12 KB", "1.4 MB". */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Icono según el carril del adjunto (o de error si el runtime lo rechazó). */
function attachmentIcon(lane: "image" | "document", rejected: boolean): ReactNode {
  if (rejected) return <AlertCircle className="size-3 shrink-0" aria-hidden />
  if (lane === "image") return <ImageIcon className="size-3 shrink-0" aria-hidden />
  return <FileText className="size-3 shrink-0" aria-hidden />
}

/** Human-readable turn duration: "42 s" or "2 min 05 s". */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  if (totalSeconds < 60) return `${totalSeconds} s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes} min ${String(seconds).padStart(2, "0")} s`
}

export function ChatMessage({
  message,
  sessionId: _sessionId,
  userName,
  imagePreviews,
  onFeedbackSubmit,
  onResolveDownloadUrl,
}: ChatMessageProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<"positive" | "negative">(
    "positive"
  )
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [copied, setCopied] = useState(false)

  // Los segmentos tal como se renderizan: un solo panel de «Razonamiento» por
  // turno. Es una transformación de presentación —no toca el dato guardado— y se
  // aplica igual al turno vivo, al recién terminado y al reabierto, así que los
  // tres se ven idénticos. Ver `groupReasoning.ts`.
  const displaySegments = useMemo(
    () => (message.segments ? groupReasoningForDisplay(message.segments) : undefined),
    [message.segments]
  )

  // Plain-text answer for the clipboard: prefer the assembled text segments
  // (excludes reasoning/tool/artifact blocks) and fall back to the raw content.
  // Se concatena sin separador, igual que `textoDeSegmentos` en `turno.js`: así lo
  // que se copia es idéntico al `content` que se guarda y a lo que se manda en el
  // feedback, sin importar si el turno se acaba de correr o se reabrió.
  const answerText = (
    message.segments && message.segments.length > 0
      ? message.segments
          .filter(seg => seg.type === "text")
          .map(seg => (seg as { content: string }).content)
          .join("")
          .trim()
      : message.content
  ) || message.content

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(answerText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked (permissions/insecure context); fail silently.
    }
  }

  const handleFeedbackClick = (type: "positive" | "negative") => {
    setSelectedFeedbackType(type)
    setIsDialogOpen(true)
  }

  const handleFeedbackSubmit = async (comment: string) => {
    await onFeedbackSubmit(selectedFeedbackType, comment)
    setFeedbackSubmitted(true)
  }

  // Reduce a (possibly presigned) URL to a stable key: origin + pathname.
  // The presigned query string (Signature, Expires, token) is noise and differs
  // run-to-run, so we strip it to compare the artifact URL against pasted images.
  const urlKey = (u?: string) => {
    if (!u) return ""
    try {
      const x = new URL(u)
      return x.origin + x.pathname
    } catch {
      return u.split("?")[0]
    }
  }

  // Remove markdown image syntax whose URL matches an artifact rendered in the
  // same message, so ArtifactCard is the single renderer of that image.
  // Presigned URLs are percent-encoded (no raw parens), so [^)] terminates the
  // capture correctly; alt text contains no `]`.
  const stripDupImages = (md: string, keys: Set<string>) =>
    md.replace(/!\[[^\]]*\]\(([^)\s]+)[^)]*\)/g, (m, url) => (keys.has(urlKey(url)) ? "" : m))

  const renderAssistantContent = () => {
    // The thinking indicator persists for the whole in-flight turn: it renders
    // below any content already streamed and stays until the stream ends (the
    // `streaming` flag is cleared on every terminal path). This is why it no
    // longer vanishes the instant the first reasoning/tool/text chunk arrives.
    //
    // Que siga ANIMADO todo el turno es la parte que importa, y no es cosmética. Un
    // turno con herramientas manda `reasoning → text → reasoning → text`, así que a
    // los pocos segundos ya hay texto del agente en pantalla; si el indicador se
    // vuelve estático ahí, la prosa intermedia («Voy a consultar su perfil
    // demográfico…») se lee como la respuesta final y el promotor cree que el turno
    // terminó cuando le faltan 60 s. La única otra animación viva es el GIF del
    // encabezado del panel de razonamiento, que queda arriba y se va de la vista en
    // cuanto se acumula texto: no sirve como señal para quien lee abajo.
    //
    // `statusLabel` deja que el turno diga en qué fase está ("Subiendo documento…",
    // "Procesando documento…"). Con adjuntos el turno tarda 3–5 min y un
    // "Pensando…" genérico no dice que la espera es por el archivo (ADR-DOC-06).
    // Cae al genérico cuando no hay fase específica — `ChatInterface` lo limpia con
    // el primer frame, así que sólo cubre la espera previa al stream.
    const hasReasoning = displaySegments?.some(s => s.type === "reasoning")
    const hasTextContent = displaySegments?.some(s => s.type === "text") || message.content
    const wasStopped = !message.streaming && hasReasoning && !hasTextContent
    const loader = message.streaming ? (
      <>
        <BrandLoader label={message.statusLabel || copy.status.thinking} />
        {hasReasoning && (
          <p className="mt-1 font-body text-[length:var(--text-caption)] text-sidebar-icon">
            {copy.status.reasoningHint}
          </p>
        )}
      </>
    ) : null

    // Nothing received yet: show only the loader while streaming. If a turn
    // ended empty (not streaming), render nothing so the bubble never sticks on
    // the "Pensando…" placeholder.
    if ((!displaySegments || displaySegments.length === 0) && !message.content) {
      return loader
    }

    let body: ReactNode
    // If segments exist, render them in order (interleaved text + tools).
    if (displaySegments && displaySegments.length > 0) {
      // Build the set of artifact URL keys ONCE from all segments. Artifacts may
      // appear after the text segment in stream order, but at render time the
      // full message has every segment.
      const artifactKeys = new Set<string>()
      for (const seg of displaySegments) {
        if (seg.type === "artifact" && seg.artifact.url) {
          artifactKeys.add(urlKey(seg.artifact.url))
        }
      }
      body = displaySegments.map((seg, i) => {
        if (seg.type === "text") {
          const content =
            artifactKeys.size > 0 ? stripDupImages(seg.content, artifactKeys) : seg.content
          return <MarkdownRenderer key={i} content={content} />
        }
        if (seg.type === "artifact") {
          const resolver = onResolveDownloadUrl?.(seg.artifact.runId, seg.artifact.name)
          return (
            <ArtifactCard
              key={i}
              artifact={seg.artifact}
              onResolveDownloadUrl={resolver ?? undefined}
            />
          )
        }
        if (seg.type === "reasoning") {
          return (
            <Reasoning
              key={i}
              stopped={wasStopped}
              streaming={!!message.streaming}
              defaultOpen={!!message.streaming}
            >
              <div className="whitespace-pre-wrap">{seg.content}</div>
            </Reasoning>
          )
        }
        const render = getToolRenderer(seg.toolCall.name)
        if (!render) return null
        return (
          <div key={seg.toolCall.toolUseId} className="my-1">
            {render({
              name: seg.toolCall.name,
              args: seg.toolCall.input,
              status: seg.toolCall.status,
              result: seg.toolCall.result,
            })}
          </div>
        )
      })
    } else {
      // Fallback: just render content as markdown.
      body = <MarkdownRenderer content={message.content} />
    }

    return (
      <>
        {body}
        {loader}
      </>
    )
  }

  const isUser = message.role === "user"
  // Turno todavía en vuelo. La barra de acciones se queda en su lugar —esconderla y
  // reaparecerla movería el layout debajo de una respuesta que se está leyendo— pero
  // copiar y calificar quedan deshabilitados: el texto aún crece, así que copiarlo da
  // una respuesta a medias y calificarlo manda al feedback algo distinto de lo que el
  // promotor acabó viendo.
  const inFlight = !!message.streaming

  return (
    <div className="flex flex-col gap-1">
      {/* User name above bubble */}
      {isUser && userName && (
        <p className="text-right text-xs font-medium text-foreground">{userName}</p>
      )}

      <MessageRow from={message.role}>
        <MessageContent from={message.role}>
          {isUser ? message.content : renderAssistantContent()}
        </MessageContent>
      </MessageRow>

      {/* Live image thumbnails for the just-sent user turn (session-only, not
          persisted — the transcript stays metadata-only). */}
      {isUser && imagePreviews && imagePreviews.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5 px-1">
          {imagePreviews.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="size-28 rounded-xl border border-chart-card-border object-cover"
            />
          ))}
        </div>
      )}

      {/* §6 estado 4 — insignia del adjunto. Antes el archivo desaparecía del
          transcript al enviar: al reabrir la conversación no quedaba rastro de que
          la respuesta se había calculado con un documento. Solo metadato. */}
      {isUser && message.attachments && message.attachments.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5 px-1">
          {message.attachments
            // When live image thumbnails are shown for this turn, skip the
            // image chips to avoid showing the same attachment twice. Document
            // chips (and images in reopened sessions with no preview) still show.
            .filter(att => !(att.lane === "image" && imagePreviews && imagePreviews.length > 0))
            .map(att => {
            const rejected = message.rejectedAttachments?.find(r => r.filename === att.filename)
            return (
              <span
                key={`${att.filename}-${att.bytes}`}
                title={
                  rejected
                    ? `${copy.errors.attachmentRejectedPrefix}: ${rejected.reason}`
                    : `${att.filename} — ${formatBytes(att.bytes)}`
                }
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                  rejected
                    ? "border-destructive/40 bg-destructive/10 text-destructive"
                    : "border-border bg-muted/50 text-muted-foreground"
                }`}
              >
                {attachmentIcon(att.lane, !!rejected)}
                <span className="max-w-[16rem] truncate">{att.filename}</span>
                {!rejected && (
                  <span className="opacity-70">{formatBytes(att.bytes)}</span>
                )}
              </span>
            )
          })}
        </div>
      )}

      {/* Aviso de adjunto no usado. El turno NO falló, así que no va por la
          superficie de error del turno — pero callarlo dejaría al usuario leyendo
          cifras que ignoran su archivo. */}
      {isUser && message.rejectedAttachments && message.rejectedAttachments.length > 0 && (
        <output className="flex flex-col gap-0.5 self-end rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs text-destructive">
          {message.rejectedAttachments.map(r => (
            <span key={r.filename}>
              {copy.errors.attachmentRejectedPrefix} «{r.filename}»: {r.reason}
            </span>
          ))}
        </output>
      )}

      {/* Timestamp + feedback for assistant messages with content. */}
      <div className={`flex items-center gap-2 px-1 ${isUser ? "justify-end" : "justify-start"}`}>
        <span className="font-body text-[10px] font-medium text-muted-foreground dark:text-timestamp">
          {formatMessageTime(message.timestamp)}
        </span>

        {!isUser && typeof message.elapsedMs === "number" && (
          <span className="font-body text-[10px] font-medium text-muted-foreground dark:text-timestamp">
            · {copy.chat.elapsedPrefix}: {formatElapsed(message.elapsedMs)}
          </span>
        )}

        {!isUser && message.content && (
          <div className="ml-2 flex items-center gap-1">
            <button
              onClick={handleCopy}
              disabled={inFlight}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={copy.actions.copy}
              title={inFlight ? copy.actions.pendingTurn : copied ? copy.actions.copied : copy.actions.copy}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <button
              onClick={() => handleFeedbackClick("positive")}
              disabled={feedbackSubmitted || inFlight}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-ok focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={copy.feedback.positiveAria}
              title={inFlight ? copy.actions.pendingTurn : copy.feedback.good}
            >
              <ThumbsUp size={14} />
            </button>
            <button
              onClick={() => handleFeedbackClick("negative")}
              disabled={feedbackSubmitted || inFlight}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-bad focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={copy.feedback.negativeAria}
              title={inFlight ? copy.actions.pendingTurn : copy.feedback.bad}
            >
              <ThumbsDown size={14} />
            </button>
            {feedbackSubmitted && (
              <span className="ml-1 text-xs text-muted-foreground">{copy.feedback.thanks}</span>
            )}
          </div>
        )}
      </div>

      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
      />
    </div>
  )
}
